import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router";
import {
  IonButtons,
  IonContent,
  IonHeader,
  IonLoading,
  IonMenuButton,
  IonPage,
  IonTitle,
  IonToast,
  IonToolbar,
} from "@ionic/react";
import {
  Box,
  TextField,
  Button,
  Table,
  TableContainer,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  Grid,
  Container,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Fade,
  Slide,
  Zoom,
  Grow,
  alpha,
  useTheme,
  styled,
  tableCellClasses,
  CircularProgress,
  Paper,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Add as AddIcon,
  Search as SearchIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  AccountBalance as BalanceIcon,
} from "@mui/icons-material";
import axios from "axios";
import { baseUrl, config, requestID } from "../../common/utils";
 import styles from "./MembersList.module.css";
import { useBusinessContext } from "../../context/business-context";

interface User {
  user_id: string;
  uname: string;
  email: string;
  phone: string;
  userName: string;
  savingAmount: number;
  sn: number;
  amount: number;
}

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    fontWeight: 600,
    fontSize: 16,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    padding: '16px',
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: alpha(theme.palette.primary.main, 0.03),
  },
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
    transform: 'translateY(-2px)',
    transition: 'all 0.3s ease',
    boxShadow: theme.shadows[2],
  },
  transition: 'all 0.3s ease',
}));

const MembersList: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const theme = useTheme();
  const { selectedBusiness } = useBusinessContext();
  console.log("Selected Business:", selectedBusiness);
  
  const name = location.pathname.split("/").pop()?.replace(/%20/g, " ") || "Members List";
  const [status, setStatus] = useState({ loading: false, error: false });
  const [users, setUsers] = useState<User[]>([]);
  const [searchQuery, setSearchQuery] = useState("");

  const fetchUsers = async () => {
    setStatus({ loading: true, error: false });
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());
      formData.append("business_id", selectedBusiness?.business_id || "");
      formData.append("branch_id", selectedBusiness?.branch_id || "");

      const response = await axios.post(
        baseUrl() + "Users/getAllUser_caas",
        formData,
        config
      );
      console.log("Response:", response);
      const { data } = response.data;
      setUsers(data);
      setStatus({ loading: false, error: false });
    } catch (error) {
      console.error("Error fetching users:", error);
      setStatus({ loading: false, error: true });
    }
  };

  const gotoCreateLedger = () => {
    // history.push("/folder/memberRegistration/MemberRegistration");
    history.push(`/folder/memberRegistration/MemberRegistration`);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const filteredData = users?.filter(
    (row) =>
      row.uname?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      row.phone?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      row.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  useEffect(() => {
    fetchUsers();
  }, []);

  return (
      <IonPage className="pushDown">
      {/* <Box className={styles.pageContainer}> */}
        {/* <IonHeader>
      <AppBar position="static" elevation={2} className={styles.appBar}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            aria-label="menu"
            className={styles.menuButton}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" component="div" className={styles.title}>
            {name}
          </Typography>
        </Toolbar>
          </AppBar>
          </IonHeader> */}
        <IonHeader className={styles.appBar}>
                <IonToolbar className={styles.headerToolbar}>
                  <IonButtons slot="start">
                    <IonMenuButton />
                  </IonButtons>
                  <IonTitle className={styles.headerTitle}>{name}</IonTitle>
                </IonToolbar>
              </IonHeader>
   <IonContent fullscreen>
     <Box className={styles.pageContainer}>
      <Container maxWidth="xl" className={styles.content}>
        <Grow in={true} timeout={800}>
          <Paper elevation={0} className={styles.paper}>
            {/* Header Section with Add Button */}
            <Box className={styles.headerSection}>
              <Slide direction="right" in={true} timeout={500}>
                <Button
                  variant="contained"
                  color="success"
                  onClick={gotoCreateLedger}
                  className={styles.addButton}
                  startIcon={<AddIcon />}
                >
                  Add New Member
                </Button>
              </Slide>
            </Box>

            {/* Search Section */}
            {users?.length > 0 && (
              <Fade in={true} timeout={700}>
                <Box className={styles.searchContainer}>
                  <SearchIcon className={styles.searchIcon} />
                  <TextField
                    label="Search by Name, Email or Phone"
                    variant="outlined"
                    fullWidth
                    value={searchQuery}
                    onChange={handleSearchChange}
                    className={styles.searchField}
                    InputProps={{
                      className: styles.searchInput,
                    }}
                  />
                </Box>
              </Fade>
            )}

            {/* Loading State */}
            {status?.loading && (
              <Box className={styles.loadingContainer}>
                <CircularProgress size={60} thickness={4} className={styles.spinner} />
                <Typography variant="h6" className={styles.loadingText}>
                  Loading members...
                </Typography>
              </Box>
            )}

            {/* Table Section */}
            {!status?.loading && users?.length > 0 && (
              <Zoom in={true} timeout={600}>
                <TableContainer component={Paper} elevation={0} className={styles.tableContainer}>
                  <Table className={styles.table}>
                    <TableHead>
                      <TableRow>
                        <StyledTableCell>S/N</StyledTableCell>
                        <StyledTableCell>
                          <Box className={styles.columnHeader}>
                            <PersonIcon className={styles.headerIcon} />
                            Name
                          </Box>
                        </StyledTableCell>
                        <StyledTableCell>
                          <Box className={styles.columnHeader}>
                            <EmailIcon className={styles.headerIcon} />
                            Contact Info
                          </Box>
                        </StyledTableCell>
                        <StyledTableCell>Username</StyledTableCell>
                        <StyledTableCell>
                          <Box className={styles.columnHeader}>
                            <BalanceIcon className={styles.headerIcon} />
                            Monthly Saving
                          </Box>
                        </StyledTableCell>
                        <StyledTableCell>No. Savings</StyledTableCell>
                        <StyledTableCell>Saving Balance</StyledTableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredData?.map((row, i) => (
                        <Grow in={true} timeout={300 + (i * 100)} key={i}>
                          <StyledTableRow className={styles.tableRow}>
                            <StyledTableCell>{i + 1}</StyledTableCell>
                            <StyledTableCell>
                              <Box className={styles.nameCell}>
                                <PersonIcon className={styles.cellIcon} />
                                {row.uname}
                              </Box>
                            </StyledTableCell>
                            <StyledTableCell>
                              <Box className={styles.contactCell}>
                                <Box className={styles.contactItem}>
                                  <EmailIcon className={styles.smallIcon} />
                                  {row.email}
                                </Box>
                                <Box className={styles.contactItem}>
                                  <PhoneIcon className={styles.smallIcon} />
                                  {row.phone}
                                </Box>
                              </Box>
                            </StyledTableCell>
                            <StyledTableCell>{row.userName}</StyledTableCell>
                            <StyledTableCell>
                              ${row.savingAmount?.toLocaleString()}
                            </StyledTableCell>
                            <StyledTableCell>{row.sn}</StyledTableCell>
                            <StyledTableCell>
                              ${row.amount?.toLocaleString()}
                            </StyledTableCell>
                          </StyledTableRow>
                        </Grow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Zoom>
            )}

            {/* Empty State */}
                {!status?.loading && (users?.length === 0 || !users) && (
              <Fade in={true} timeout={500}>
                <Box className={styles.emptyState}>
                  <PersonIcon className={styles.emptyIcon} />
                  <Typography variant="h6" className={styles.emptyText}>
                    No members found
                  </Typography>
                  <Typography variant="body2" className={styles.emptySubtext}>
                    Start by adding your first member
                  </Typography>
                </Box>
              </Fade>
            )}

            {/* No Results State */}
            {!status?.loading && users?.length > 0 && filteredData?.length === 0 && (
              <Fade in={true} timeout={500}>
                <Box className={styles.emptyState}>
                  <SearchIcon className={styles.emptyIcon} />
                  <Typography variant="h6" className={styles.emptyText}>
                    No matching members found
                  </Typography>
                  <Typography variant="body2" className={styles.emptySubtext}>
                    Try adjusting your search criteria
                  </Typography>
                </Box>
              </Fade>
            )}
          </Paper>
        </Grow>
        </Container>
        </Box>
      </IonContent>
    {/* </Box> */}
    </IonPage>
  );
};

export default MembersList;